import { useRef, useEffect } from 'react';
import styled from 'styled-components';

import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useMatchMedia } from '@xing-com/crate-hooks-use-match-media';
import { InputBar } from '@xing-com/text-field';
import {
  scale010,
  xdlColorControlBorderSecondary,
  xdlColorSliderTickMark,
  xdlColorText,
} from '@xing-com/tokens';

import { useAnimatedValue } from '../hooks';

type Props = {
  value: string;
  isAnimating: boolean;
  onCompleted: () => void;
};

export const QueryInputAnimation: React.FC<Props> = ({
  value,
  isAnimating,
  onCompleted,
}) => {
  const { isLoggedIn } = useLoginState();
  const isAnimatingPrev = useRef(isAnimating);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { run, cancel } = useAnimatedValue(inputRef, value, {
    onCompleted,
  });

  useEffect(() => {
    if (isAnimating) {
      run();
    } else if (isAnimatingPrev.current) {
      cancel();
    }

    isAnimatingPrev.current = isAnimating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnimating]);

  if (useMatchMedia('(prefers-reduced-motion: reduce)') || !isLoggedIn) {
    return null;
  }

  const handleOnClick = (): void => {
    cancel();
  };

  const handleOnMouseDown = (e: React.MouseEvent): void => {
    // prevent input focus -> unfocus -> focus
    e.preventDefault();
  };

  return (
    <InputAnimation
      variant="plain"
      multiline
      size="large"
      value=" " // using a space to display the close button
      ref={inputRef}
      onClick={handleOnClick}
      onMouseDown={handleOnMouseDown}
      aria-hidden
      tabIndex={-1}
      clearButtonProps={{
        onClick: handleOnClick,
        onMouseDown: handleOnMouseDown,
        'aria-hidden': true,
        tabIndex: -1,
      }}
      $show={isAnimating}
    />
  );
};

export const InputAnimation = styled(InputBar)<{ $show: boolean }>`
  display: ${({ $show }) => ($show ? 'block' : 'none')};
  position: absolute;
  inset: 0;

  & textarea {
    min-height: 100%;
    color: ${xdlColorText};
    border-color: ${xdlColorControlBorderSecondary};
    border-width: ${scale010};
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    scrollbar-width: thin;
    scrollbar-color: ${xdlColorSliderTickMark} transparent;
  }
`;
